import { useState } from 'react'
import type { FieldDefinition } from '@/features/contract/types'
import type { FieldVerificationItem, SubjectType } from '../types'
import { FieldVerificationBadge, type BadgeState } from './FieldVerificationBadge'
import { VerifyDialog } from './VerifyDialog'
import { VerificationDetailsModal } from './VerificationDetailsModal'

/**
 * Slot per pole - renderuje ptaszek i zarządza state dialogów (Verify / Details).
 * Cienki wrapper, używany przez SectionForm dla każdego pola z włączoną weryfikacją.
 */
export function SectionFieldVerificationSlot({
  field,
  subjectType,
  subjectId,
  currentValue,
  verification,
  canVerify,
}: {
  field: FieldDefinition
  subjectType: SubjectType
  subjectId: number
  currentValue: string | null
  verification: FieldVerificationItem | null
  canVerify: boolean
}) {
  const [openDialog, setOpenDialog] = useState<'verify' | 'details' | null>(null)
  const state: BadgeState = computeState(verification, currentValue)

  return (
    <>
      <FieldVerificationBadge
        state={state}
        canVerify={canVerify}
        verification={verification}
        verifierRoleLabels={field.verifierRoleCodes}
        onVerifyClick={() => setOpenDialog('verify')}
        onViewDetailsClick={() => setOpenDialog('details')}
      />
      {openDialog === 'verify' && (
        <VerifyDialog
          field={field}
          subjectType={subjectType}
          subjectId={subjectId}
          currentValue={currentValue}
          onClose={() => setOpenDialog(null)}
        />
      )}
      {openDialog === 'details' && verification && (
        <VerificationDetailsModal
          verificationId={verification.id}
          subjectType={subjectType}
          subjectId={subjectId}
          fieldLabel={field.labelPl}
          canRevoke={canVerify}
          onClose={() => setOpenDialog(null)}
        />
      )}
    </>
  )
}

function computeState(
  v: FieldVerificationItem | null,
  currentValue: string | null,
): BadgeState {
  if (!v || !v.isActive) return 'unverified'
  // Backend invalidate-on-change zamienia weryfikację na inactive po edycji wartości.
  // Dodatkowy soft check po stronie FE: jeśli wartość różni się od snapshot, traktuj
  // jako requires_attention (UI optymistycznie - finalny stan przyjdzie z invalidate query).
  // W list endpoint nie ma snapshotu, więc state=verified jest dopuszczalny - revalidacja
  // następuje po invalidate query po edycji.
  return currentValue === '' || currentValue == null ? 'requires_attention' : 'verified'
}
